import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHandlerFn,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AppContext } from '@osos/shared/types';
import { EMPTY, Observable, throwError, timer } from 'rxjs';
import { catchError, retry, switchMap } from 'rxjs/operators';
import { ConfigService } from '../../config';
import { injectAuthService } from '../../services/auth/provide-auth-service';
import {
  AppState,
  AuthState,
  LanguageState,
  SignIn,
  SignOut,
  UserPreferencesActions,
  UserSessionExpired,
} from '../../state';

export abstract class AuthInterceptorBase implements HttpInterceptor {
  protected _currentLang: string;
  protected _timeZoneOffset: number;
  protected authService = injectAuthService();
  protected store = inject(Store);
  protected configService = inject(ConfigService);
  protected router = inject(Router);
  protected route = inject(ActivatedRoute);
  constructor() {
    this.store.select(LanguageState.currentLanguage).subscribe((lang) => {
      this._currentLang = lang?.languageName ?? 'en';
    });
    this.setTimeZoneOffset();
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this._isApiRequest(request.url)) {
      return next.handle(request);
    }

    return this.getAccessToken()?.pipe(
      switchMap((token) => this._interceptRequest(request, token, next.handle)),
      retry({
        delay: (error: any, retryCount: number) => {
          if (error.status === 401) {
            if (this.authService.isUserSessionExpired()) {
              this.store.dispatch(new UserSessionExpired());
            } else if (retryCount <= 3) {
              return timer(retryCount * 5000);
            } else {
              this.store.dispatch(new SignOut());
            }
          }

          return throwError(() => error);
        },
      }),
      catchError((error: unknown) => {
        return throwError(() => error);
      }),
    );
  }

  private _interceptRequest(
    req: HttpRequest<any>,
    token: string,
    next: HttpHandlerFn,
  ): Observable<HttpEvent<unknown>> {
    req = this._cloneRequestWithBearerTokenAndAdditionalHeaders(req, token);

    return next(req).pipe(
      catchError((error: unknown): Observable<HttpEvent<any>> => {
        const errorResponse = error as HttpErrorResponse;

        if (errorResponse.status === 403) {
          //get the app context

          let availableSpaces = [];

          const error = errorResponse.error.error;
          if (error.code === 'SPACE_FORBIDDEN') {
            availableSpaces = error.details?.available_spaces ?? [];
          }
          if (error.code === 'DELEGATION_EXPIRED') {
            this.store.dispatch(
              new UserPreferencesActions.SetActiveDelegation(null),
            );
          }
          const appContext = this.store.selectSnapshot(
            AppState.currentAppContext,
          );
          if (!this.store.selectSnapshot(AuthState.isAuthenticated)) {
            this.store.dispatch(new SignIn());
            // return throwError(() => errorResponse);
            return EMPTY;
          }
          if (appContext !== AppContext.Public) {
            this.router.navigate(['403'], {
              state: {
                isPublicAccessForbidden: true,
                availableSpaces,
              },
            });
          } else {
            this.router.navigate(['403'], {
              state: {
                availableSpaces,
              },
            });
          }
        }

        return throwError(() => errorResponse);
      }),
    );
  }

  private _cloneRequestWithBearerTokenAndAdditionalHeaders(
    request: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    const headers = {
      'Accept-Language': this._currentLang,
      'X-TZ': this._timeZoneOffset.toString(),
    };

    if (token) {
      const { headerKey, headerValue } = this.setTokenHeader(token);
      headers[headerKey] = headerValue;
    }

    const additionalHeaders = this.setAdditionalHeaders();
    additionalHeaders.forEach(({ headerKey, headerValue }) => {
      headers[headerKey] = headerValue;
    });

    return request.clone({ setHeaders: headers });
  }

  private _isApiRequest(url: string): boolean {
    return this.configService
      .getApiBaseUrls()
      .some((baseUrl) => url.startsWith(baseUrl));
  }

  abstract setTimeZoneOffset(): void;
  abstract setTokenHeader(token: string): {
    headerKey: string;
    headerValue: string;
  };
  abstract setAdditionalHeaders(): Array<{
    headerKey: string;
    headerValue: string;
  }>;
  abstract getAccessToken(): Observable<string>;
}
